import React from 'react';
import { Link } from 'react-router-dom';
import { AirVent, Tv, WashingMachine, Box, Droplets, Wrench, MapPin } from 'lucide-react';
import type { Brand } from '../../types';
import { services } from '../../data/services';
import { brandServiceKeywords } from '../../data/brandServiceKeywords';
import ActionButtons from '../shared/ActionButtons';

interface BrandServiceListProps {
  brand: Brand;
}

const serviceIcons = {
  'ac-repair': AirVent,
  'tv-repair': Tv,
  'washing-machine-repair': WashingMachine,
  'refrigerator-repair': Box,
  'water-purifier-repair': Droplets
};

export default function BrandServiceList({ brand }: BrandServiceListProps) {
  const brandKeywords = brandServiceKeywords[brand.id] || {};

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {brand.name} Repair Service Center
          </h2>
          <p className="text-lg text-gray-600">
            Professional repair service center for all {brand.name} appliances
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service) => {
            const Icon = serviceIcons[service.id as keyof typeof serviceIcons];
            const keywords = brandKeywords[service.id] || [];

            return (
              <div 
                key={service.id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-500 p-6 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg">
                    {Icon && <Icon className="h-6 w-6 text-blue-600" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {brand.name} {service.name} Service Center
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Professional repair service center for all {brand.name} models
                    </p>

                    {keywords.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {keywords.map((keyword, index) => (
                            <span 
                              key={index}
                              className="text-sm bg-gray-50 text-gray-600 px-2 py-1 rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">90-Day Warranty</span>
                      <span className="text-sm bg-green-50 text-green-700 px-2 py-1 rounded">Same Day Service</span>
                    </div>
                    
                    <Link
                      to={`/brands/${brand.id}/${service.id}`}
                      className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
                    >
                      View Details
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            Need {brand.name} Appliance Repair Service Center?
          </h3>
          <p className="text-lg opacity-90 mb-8">
            Our expert technicians are available 24/7 for emergency repairs
          </p>
          <ActionButtons className="justify-center" />
        </div>
      </div>
    </div>
  );
}